import React from 'react';
import { observer } from 'mobx-react-lite';
import { useSortable } from '@dnd-kit/react/sortable';
import { CollisionPriority } from '@dnd-kit/abstract';
import { Pencil1Icon, TrashIcon } from '@radix-ui/react-icons';

import { StageHeader, StageNumber } from './stage-header';
import { CreateStepIndicator } from './create-step-indicator';
import { DropdownMenu } from '../../../components/modals/dropdown-menu/DropdownMenu';
import { RenameDialog } from '../../../components/dialogs/rename.dialog';
import { DeleteStageDialog } from './delete-stage.dialog';
import { openModal } from '../../../core/modal/open-modal';
import { catchError } from '../../../core/catch-error';
import { Stage as StageModel } from '../../../app/entities/stage';

import styles from './stage.module.scss';
interface PhaseProps {
  stageId: string;
  index: number;
  children?: React.ReactNode;
}

export const Stage: React.FC<PhaseProps> = observer(({ stageId, index, children }) => {
  const { ref } = useSortable({
    id: stageId,
    index,
    type: 'column',
    accept: ['column', 'item'],
    collisionPriority: CollisionPriority.Low,
  });

  const stage = StageModel.getOne(stageId)!;

  if (!stage) {
    return;
  }

  const handleRename = async (name: string) => {
    try {
      await stage.update({ name });
    } catch (e) {
      catchError(e);
    }
  };

  const handleDelete = async () => {
    try {
      await stage.delete();
    } catch (e) {
      catchError(e);
    }
  };

  const menuItems = [
    {
      title: 'Rename',
      icon: <Pencil1Icon />,
      onSelect: () =>
        openModal(RenameDialog, {
          defaultName: stage.name,
          onSubmit: handleRename,
          title: 'Rename stage',
          label: 'Stage name',
        }),
    },
    {
      title: 'Delete',
      icon: <TrashIcon />,
      type: 'danger' as const,
      onSelect: () =>
        openModal(DeleteStageDialog, {
          stageName: stage.name,
          stepsCount: stage.totalSteps,
          onSubmit: handleDelete,
        }),
    },
  ];

  return (
    <div className={styles['container']} id={stageId} ref={ref}>
      <div className={styles['header']}>
        <StageHeader
          name={stage.name}
          suffix={<StageNumber isActive>{stage.order.toString().padStart(2, '0')}</StageNumber>}
          completed={stage.completedSteps}
          total={stage.totalSteps}
          isActive
          actions={<DropdownMenu items={menuItems} />}
        />
      </div>

      {children}

      <CreateStepIndicator stageId={stage._id} />
    </div>
  );
});
